<template>
  <teleport to="body">
    <!-- Overlay -->
    <transition name="fade">
      <div v-if="isOpen" class="drawer-overlay" @click="close"></div>
    </transition>

    <!-- Sliding Drawer -->
    <transition name="slide-right">
      <div v-if="isOpen" class="drawer-panel">

        <!-- Header -->
        <div class="drawer-header">
          <div class="user-greeting">
            <div class="avatar">{{ customerInitial }}</div>
            <div>
              <h2 class="greeting-text">{{ $t('customerDrawer.greeting', { name: customerName }) }}</h2>
              <p class="greeting-sub">{{ $t('customerDrawer.bookingHub') }}</p>
            </div>
          </div>
          <div class="header-actions">
            <a :href="settingsUrl" class="icon-btn-header" :aria-label="$t('customerDrawer.accountSettings')">
              <i class="fa fa-cog"></i>
            </a>
            <button @click="close" class="close-btn"><i class="fa fa-times"></i></button>
          </div>
        </div>

        <div class="drawer-body">

          <!-- Loading -->
          <div v-if="loading" class="empty-card">
            {{ $t('customerDrawer.loadingBookings') }}
          </div>

          <!-- Error -->
          <div v-else-if="error" class="empty-card">
            {{ error }}
          </div>

          <template v-else>
            <!-- Upcoming Appointment (Priority) -->
            <div class="section-block">
              <h3 class="section-eyebrow">{{ $t('customerDrawer.upcomingAppointment') }}</h3>
              <div v-if="upcomingVisit" class="glass-card active-booking">
                <div class="booking-date">
                  <span class="date-month">{{ upcomingVisit.month }}</span>
                  <span class="date-day">{{ upcomingVisit.day }}</span>
                </div>
                <div class="booking-info">
                  <span class="booking-title">{{ upcomingVisit.service }}</span>
                  <span class="booking-meta">{{ $t('customerDrawer.withBarberAtTime', { barber: upcomingVisit.barber, time: upcomingVisit.time }) }}</span>
                </div>
              </div>
              <div v-else class="empty-card">
                {{ $t('customerDrawer.noUpcomingAppointments') }}
              </div>
            </div>

            <!-- Visit History -->
            <div class="section-block">
              <h3 class="section-eyebrow">{{ $t('customerDrawer.pastVisits') }}</h3>
              <div v-if="pastVisits.length" class="history-list">
                <div v-for="visit in pastVisits" :key="visit.id" class="history-row">
                  <div class="history-info">
                    <span class="history-title">{{ visit.service }}</span>
                    <span class="history-meta">{{ visit.date }} • {{ visit.barber }}</span>
                  </div>
                  <button @click="rebook(visit)" class="dash-btn btn-primary-solid btn-sm">
                    {{ $t('customerDrawer.rebook') }}
                  </button>
                </div>
              </div>
              <div v-else class="empty-card">
                {{ $t('customerDrawer.noPastVisits') }}
              </div>
            </div>
          </template>

        </div>

        <!-- Footer / Logout -->
        <div class="drawer-footer">
          <button @click="handleLogout" class="justify-center w-full dash-btn btn-danger-outline">
            <i class="fa fa-sign-out-alt"></i> {{ $t('customerDrawer.logout') }}
          </button>
        </div>

      </div>
    </transition>
  </teleport>
</template>

<script>
import { Auth } from '../services/index.js';
import axios from 'axios';

export default {
  name: 'CustomerDrawer',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      rawBookings: [],
      loading: false,
      error: null,
      settingsUrl: 'https://book.barbersunity.com/customer-profile',
      MONTHS: [],
    }
  },
  created() {
    this.MONTHS = [
      this.$t('months_short.jan'), this.$t('months_short.feb'), this.$t('months_short.mar'),
      this.$t('months_short.apr'), this.$t('months_short.may'), this.$t('months_short.jun'),
      this.$t('months_short.jul'), this.$t('months_short.aug'), this.$t('months_short.sep'),
      this.$t('months_short.oct'), this.$t('months_short.nov'), this.$t('months_short.dec')
    ];
  },
  computed: {
    customerName() {
      const user = Auth.getUser();
      return user?.name || this.$t('customerDrawer.guest');
    },
    customerInitial() {
      return this.customerName.charAt(0).toUpperCase();
    },
    // Parse each raw booking's "YYYY-MM-DD_HH:mm" dateTime into a real Date,
    // dropping anything that fails to parse.
    parsedBookings() {
      return this.rawBookings
        .map(b => {
          const [datePart, timePart] = (b.dateTime || '').split('_');
          const dt = new Date(`${datePart}T${timePart || '00:00'}`);
          return { ...b, _date: dt, _timePart: timePart || '' };
        })
        .filter(b => !isNaN(b._date.getTime()));
    },
    upcomingVisit() {
      const now = new Date();
      const future = this.parsedBookings
        .filter(b => b._date >= now)
        .sort((a, b) => a._date - b._date);
      if (!future.length) return null;
      const next = future[0];
      return {
        month: this.MONTHS[next._date.getMonth()],
        day: String(next._date.getDate()).padStart(2, '0'),
        // NOTE: bookings currently don't store a "service" field on the
        // backend (/book only saves worker, dateTime, and contact info),
        // so there's no real service name to show here yet.
        service: this.$t('customerDrawer.appointment'),
        barber: next.worker || this.$t('customerDrawer.unknown'),
        time: next._timePart,
        salonUrl: next.partner
      };
    },
    pastVisits() {
      const now = new Date();
      return this.parsedBookings
        .filter(b => b._date < now)
        .sort((a, b) => b._date - a._date)
        .map((b, i) => ({
          id: `${b.partner}-${b.dateTime}-${i}`,
          service: this.$t('customerDrawer.appointment'),
          barber: b.worker || this.$t('customerDrawer.unknown'),
          date: b._date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }),
          salonUrl: b.partner
        }));
    }
  },
  watch: {
    isOpen(val) {
      if (val) this.fetchBookings();
    }
  },
  methods: {
    async fetchBookings() {
      this.loading = true;
      this.error = null;
      try {
        const token = Auth.getToken();
        const response = await axios.get('https://api.barbersunity.com/customer/bookings', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        this.rawBookings = response.data || [];
      } catch (e) {
        console.error('Error fetching bookings:', e);
        this.error = this.$t('customerDrawer.couldNotLoadBookings');
        this.rawBookings = [];
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.$emit('close');
    },
    handleLogout() {
      this.close();
      Auth.logout();
    },
    rebook(visit) {
      this.close();
      // Route them directly back to that specific salon's page
      this.$router.push(`/eng/${visit.salonUrl}`);
    }
  }
}
</script>

<style scoped>
/* ── DESIGN TOKENS ── */
.drawer-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(4, 6, 14, 0.6); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
}

.drawer-panel {
  position: fixed; top: 0; right: 0; bottom: 0; z-index: 10000;
  width: 100%; max-width: 420px;
  background: rgba(15, 20, 32, 0.85); backdrop-filter: blur(32px); -webkit-backdrop-filter: blur(32px);
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.5);
  display: flex; flex-direction: column;
}

/* ── HEADER ── */
.drawer-header {
  padding: 2rem 1.5rem 1.5rem; border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex; align-items: flex-start; justify-content: space-between;
}
.user-greeting { display: flex; align-items: center; gap: 1rem; }
.avatar { width: 48px; height: 48px; border-radius: 14px; background: rgba(74, 127, 165, 0.15); color: #6499bb; font-size: 1.2rem; font-weight: 800; display: flex; align-items: center; justify-content: center; }
.greeting-text { margin: 0; font-size: 1.25rem; font-weight: 800; color: #f0f4f8; }
.greeting-sub { margin: 0; font-size: 0.8rem; color: #94a3b8; }
.header-actions { display: flex; align-items: center; gap: 0.5rem; }
.icon-btn-header { display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.05); color: #94a3b8; width: 32px; height: 32px; border-radius: 50%; cursor: pointer; transition: all 0.2s; text-decoration: none; }
.icon-btn-header:hover { background: rgba(255,255,255,0.1); color: #fff; }
.close-btn { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.05); color: #94a3b8; width: 32px; height: 32px; border-radius: 50%; cursor: pointer; transition: all 0.2s; }
.close-btn:hover { background: rgba(255,255,255,0.1); color: #fff; transform: rotate(90deg); }

/* ── BODY ── */
.drawer-body { flex: 1; overflow-y: auto; padding: 1.5rem; display: flex; flex-direction: column; gap: 2rem; }
.section-eyebrow { font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 1rem 0; }

.glass-card { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); border-radius: 16px; padding: 1.25rem; }
.active-booking { display: flex; align-items: center; gap: 1rem; border-color: rgba(74, 127, 165, 0.3); background: linear-gradient(135deg, rgba(74, 127, 165, 0.1), transparent); box-shadow: 0 8px 24px rgba(74, 127, 165, 0.1); }
.booking-date { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 50px; height: 50px; background: rgba(0,0,0,0.3); border-radius: 12px; }
.date-month { font-size: 0.6rem; font-weight: 700; color: #6499bb; text-transform: uppercase; }
.date-day { font-size: 1.2rem; font-weight: 800; color: #fff; line-height: 1; }
.booking-info { flex: 1; display: flex; flex-direction: column; }
.booking-title { font-size: 1rem; font-weight: 700; color: #fff; }
.booking-meta { font-size: 0.75rem; color: #94a3b8; }
.empty-card { padding: 1.5rem; text-align: center; border: 1px dashed rgba(255,255,255,0.1); border-radius: 16px; color: #64748b; font-size: 0.85rem; }

.history-list { display: flex; flex-direction: column; gap: 0.5rem; }
.history-row { display: flex; align-items: center; justify-content: space-between; padding: 1rem; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 14px; transition: background 0.2s; }
.history-row:hover { background: rgba(255,255,255,0.05); }
.history-info { display: flex; flex-direction: column; }
.history-title { font-size: 0.95rem; font-weight: 600; color: #e2e8f0; }
.history-meta { font-size: 0.75rem; color: #64748b; }

/* ── BUTTONS & FOOTER ── */
.drawer-footer { padding: 1.5rem; border-top: 1px solid rgba(255, 255, 255, 0.05); }
.dash-btn { display: inline-flex; align-items: center; gap: 8px; padding: 0.85rem 1.5rem; border: none; border-radius: 12px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
.btn-sm { padding: 0.4rem 0.85rem; font-size: 0.8rem; border-radius: 8px; }
.btn-primary-solid { background: #e2e8f0; color: #000; box-shadow: 0 4px 12px rgba(255,255,255,0.1); }
.btn-primary-solid:hover { background: #fff; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(255,255,255,0.2); }
.btn-danger-outline { background: transparent; border: 1px solid rgba(239, 68, 68, 0.3); color: #f87171; }
.btn-danger-outline:hover { background: rgba(239, 68, 68, 0.1); border-color: rgba(239, 68, 68, 0.5); }
.w-full { width: 100%; } .justify-center { justify-content: center; }

/* ── ANIMATIONS ── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.slide-right-enter-active, .slide-right-leave-active { transition: transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1); }
.slide-right-enter-from, .slide-right-leave-to { transform: translateX(100%); }
</style>